/* eslint-disable react/require-default-props */

import React from 'react';
import {
  Box,
  Button,
  CardHeader,
  Icon,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import { RuleImpact } from '../../../../shared';
import { useInvertedLocalize } from '../../../../utils/localization';

import invertedMessages from './messages';

import './RuleCardHeader.css';

type ImpactLevel = 'low' | 'medium' | 'high';

interface RuleCardHeaderProps {
  userCount: number; // num users matched by this rule
  impact?: ImpactLevel;
  onIgnoreButtonClick: () => void;
  isIgnoreButtonDisabled?: boolean;
}

/**
 * RuleCardHeader component displaying the top section of a rule card
 * with security icon, user count, impact badge, and menu button
 *
 * @param props - The component props
 * @returns JSX element containing the rule card header
 */
function RuleCardHeader({
  userCount,
  impact,
  onIgnoreButtonClick,
  isIgnoreButtonDisabled = false,
}: RuleCardHeaderProps) {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <CardHeader className='Rules-RuleCardHeader'>
      <Box className='Rules-RuleCardHeader-content'>
        {/* Left section: Icon and user count */}
        <Box className='Rules-RuleCardHeader-left'>
          <Icon
            kind='Security'
            size='medium'
            color='--color-foreground-neutral-bold'
          />
          <Typography
            kind='h3'
            color='--color-foreground-neutral-bold'
            className='Rules-RuleCardHeader-userCount'>
            {l(
              '{count, plural, one {# Included User} other {# Included Users}}',
              { count: userCount }
            )}
          </Typography>
          {impact && <RuleImpact impactLevel={impact} iconSize='small' />}
        </Box>

        {/* Right section: Impact badge and menu */}
        <Box className='Rules-RuleCardHeader-right'>
          <Tooltip
            text={
              isIgnoreButtonDisabled
                ? l(
                    "You don't have the permissions to ignore or the feature is turned off."
                  )
                : ''
            }
            isDisabled={!isIgnoreButtonDisabled}
            placement='top'>
            <Button
              kind='ghost'
              size='medium'
              onClick={onIgnoreButtonClick}
              isDisabled={isIgnoreButtonDisabled}
              className='Rules-RuleCardHeader-ignoreButton'>
              {l('Ignore')}
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </CardHeader>
  );
}

export default RuleCardHeader;
